import { useCallback, useEffect, useRef, useState } from "react";
import api from "../api/api";

export interface AppNotification {
  id: string | number;
  data: {
    title?: string;
    message?: string;
    type?: string;
  };
  read_at?: string | null;
  created_at?: string | null;
}

export interface UseNotificationsResult {
  notifications: AppNotification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  markRead: (id: string | number) => Promise<void>;
  markAllRead: () => Promise<void>;
}

interface UseNotificationsOptions {
  pollIntervalMs?: number;
}

export const formatTimeAgo = (dateString: string | null | undefined): string => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString("en-IN");
};

/**
 * Wire the header notification bell to the backend notification endpoints.
 *
 * Endpoints consumed (code defensively — tolerate missing/failing endpoints):
 *   GET  /notifications                       -> paginated list
 *   GET  /notifications/unread-count          -> { data: { count } }
 *   POST /notifications/{id}/mark-read
 *   POST /notifications/mark-all-read
 */
export const useNotifications = (
  options: UseNotificationsOptions = {}
): UseNotificationsResult => {
  const pollIntervalMs = options.pollIntervalMs ?? 60000;
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef<boolean>(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const extractList = (payload: any): AppNotification[] => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.data?.data)) return payload.data.data;
    return [];
  };

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const [listRes, countRes] = await Promise.allSettled([
        api.get("/notifications", { params: { per_page: 8 } }),
        api.get("/notifications/unread-count"),
      ]);

      if (!mountedRef.current) return;

      if (listRes.status === "fulfilled") {
        setNotifications(extractList(listRes.value.data));
        setError(null);
      } else {
        setError("Failed to load notifications");
      }

      if (countRes.status === "fulfilled") {
        const payload = countRes.value.data;
        const count =
          payload?.data?.count ??
          payload?.count ??
          payload?.data?.unread_count ??
          payload?.unread_count;
        setUnreadCount(Number(count) || 0);
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  const markRead = useCallback(async (id: string | number): Promise<void> => {
    try {
      await api.post(`/notifications/${id}/mark-read`);
      if (!mountedRef.current) return;
      setNotifications((prev) =>
        prev.map((item) => (item.id === id ? { ...item, read_at: new Date().toISOString() } : item))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      // Non-fatal: badge state stays as-is until next poll
    }
  }, []);

  const markAllRead = useCallback(async (): Promise<void> => {
    try {
      await api.post("/notifications/mark-all-read");
      if (!mountedRef.current) return;
      setNotifications((prev) =>
        prev.map((item) => ({ ...item, read_at: item.read_at || new Date().toISOString() }))
      );
      setUnreadCount(0);
    } catch (err) {
      // Non-fatal
    }
  }, []);

  useEffect(() => {
    refresh();
    const intervalId = window.setInterval(refresh, pollIntervalMs);
    return () => window.clearInterval(intervalId);
  }, [refresh, pollIntervalMs]);

  return { notifications, unreadCount, loading, error, refresh, markRead, markAllRead };
};

export default useNotifications;
